import { amRoutes, ucEndpoints } from "../../config/config";
import { delayBeforeRedirect } from "../../config/constants";
import { AppDispatch } from "../../reducers";
import { history } from "../../store";
import { resetApp } from "../../store/appStatus";
import {
  checkPasswordResetTokenStart,
  checkPasswordResetTokenSuccess,
  passwordResetConfirmFailed,
  passwordResetConfirmStart,
  passwordResetConfirmSuccess,
  setPasswordResetToken
} from "../../store/confirmPasswordReset";
import {
  hideSuccessMessage,
  showFailureMessage,
  showSuccessMessage
} from "../../store/statusMessages";
import { UserClaims } from "../../types/users";
import { getErrorMessageFromRequest } from "../../utils/utils";
import { apiCallBegan } from "../middlwares/api";
import { setShortToken } from "./users";

const clearAndRedirect = (dispatch: AppDispatch) => {
  localStorage.clear();
  dispatch(resetApp());
  history.push(amRoutes.dashboard);
};

/**
 * Checks password reset token and sets UC's token.
 *
 * @param {String} passwordResetToken   - Token to check.
 * @return {Function}                   - Redux action.
 */
export const checkPasswordResetToken = (passwordResetToken: string) => (dispatch: AppDispatch) => {
  dispatch(setPasswordResetToken(passwordResetToken));
  dispatch(
    apiCallBegan({
      url: ucEndpoints.checkPasswordResetToken,
      method: "post",
      data: { passwordResetToken },
      onStart: checkPasswordResetTokenStart,
      onSuccess: ({
        token,
        shortToken,
        user
      }: {
        token: string;
        shortToken: string;
        user: UserClaims;
      }) => (dispatch: AppDispatch) => {
        setShortToken(shortToken);
        dispatch(checkPasswordResetTokenSuccess(token, user));
        localStorage.setItem("ucToken", token);
      },
      onFailure: (error: any) => (dispatch: AppDispatch) => {
        dispatch(
          showFailureMessage(
            "Password reset token validation failed",
            `${getErrorMessageFromRequest(error)}. Redirecting...`
          )
        );
        setTimeout(() => clearAndRedirect(dispatch), delayBeforeRedirect);
      }
    })
  );
};

/**
 * Sends password reset confirmation request.
 *
 * @param {string} passwordResetToken  - Token.
 * @param {string} password            - New password.
 * @return                             - Redux action.
 */
export const finishPasswordReset = (passwordResetToken: string, password: string) => (
  dispatch: AppDispatch
) =>
  dispatch(
    apiCallBegan({
      url: ucEndpoints.finishPasswordReset,
      method: "post",
      data: { passwordResetToken, pass: password },
      onStart: () => (dispatch: AppDispatch) => dispatch(passwordResetConfirmStart(password)),
      onSuccess: () => (dispatch: AppDispatch) => {
        dispatch(passwordResetConfirmSuccess());
        dispatch(showSuccessMessage("Password successfully reset!", "Redirecting..."));
        setTimeout(() => {
          dispatch(hideSuccessMessage());
          localStorage.clear();
          history.push(amRoutes.dashboard);
        }, delayBeforeRedirect);
      },
      onFailure: (error: any) => (dispatch: AppDispatch) => {
        dispatch(
          passwordResetConfirmFailed(`${getErrorMessageFromRequest(error)}. Redirecting...`)
        );
        setTimeout(() => clearAndRedirect(dispatch), delayBeforeRedirect);
      }
    })
  );
